import React from 'react';

/**
 * Rich-format a user-typed sender note. Two markdown-ish markers are
 * supported: `*text*` → <strong>text</strong> and `_text_` → <em>text</em>.
 * Nothing else is recognised — no links, no headings, no code fences.
 *
 * The parser emits React NODES (never innerHTML). Every branch that
 * outputs user text does so as a React text child so React's built-in
 * auto-escaping handles any hostile input. Delimiters are non-nested
 * and single-line — a `\n` in the raw text terminates both markers, so
 * `*foo\nbar*` is emitted as plain text.
 *
 * @param {string} text
 * @returns {React.ReactNode[] | string}
 */
export function renderSenderNoteMarkup(text) {
  if (typeof text !== 'string' || !text) return text;
  const nodes = [];
  let key = 0;
  // Match the earliest *bold* or _italic_ single-line span. Requires at
  // least one non-marker char inside so bare "**" or "__" is left alone.
  const RE = /\*([^*\n]+)\*|_([^_\n]+)_/;
  let remainder = text;
  let m;
  while ((m = RE.exec(remainder)) !== null) {
    if (m.index > 0) nodes.push(remainder.slice(0, m.index));
    if (m[1] !== undefined) {
      nodes.push(<strong key={`b-${key++}`}>{m[1]}</strong>);
    } else {
      nodes.push(<em key={`i-${key++}`}>{m[2]}</em>);
    }
    remainder = remainder.slice(m.index + m[0].length);
  }
  if (remainder.length) nodes.push(remainder);
  return nodes;
}

/**
 * Plain-text version of a sender note: removes the `*bold*` / `_italic_`
 * wrappers (keeping the inner text) so it can be drawn as flat text where
 * rich runs aren't practical (e.g. jsPDF vector text). Mirrors the RE used
 * by renderSenderNoteMarkup so both stay in sync.
 */
export function stripSenderNoteMarkup(text) {
  if (typeof text !== 'string' || !text) return '';
  return text.replace(/\*([^*\n]+)\*/g, '$1').replace(/_([^_\n]+)_/g, '$1');
}
